/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { cartActions } from "../modules/cart";
import type { Cart as CartType, Product } from "../hooks/cart/Cart";
import { useStickyCartSummary } from "../hooks/cart/StickyCartSummary";
import { CartHeading } from "../components/cart/CartHeading";
import { CartOrderHeading } from "../components/cart/CartOrderHeading";
import { CartOrderProduct } from "../components/cart/CartOrderProduct";
import CloseIcon from "@material-ui/icons/Close";
import { useI18n } from "../hooks/i18n/i18n";
import { useHistory } from "react-router";
import { SPView } from "../components/common/MediaQuery";
import { CartStickyOrder } from "../components/cart/CartStickyOrder";
import { RemovedCartItemsMessage } from "./RemovedCartItemsMessage";

import { usePushViewCartEventToGA } from "utility/ga/pushEvent";

/**
 * カートモーダル内のカートコンポーネント
 * 既存チケット、グッズとテナントグッズのカートを表示する
 * Copy from https://github.com/balus-co-ltd/spwn/blob/26317480e8f8885c80434362b8b827c19b6a9f2a/packages/portal/src/containers/cart/Cart.tsx#L55
 */
export const Cart: React.FC<{
  cart: CartType;
  deleteFromCart: (product: Product) => void;
}> = ({ cart, deleteFromCart }) => {
  const { t } = useI18n();
  const classes = styles();
  const history = useHistory();
  const dispatch = useDispatch();

  const { headerRefs, stickyCartIndex } = useStickyCartSummary(cart);
  const { orderCarts, cartItemCount } = cart;
  const isDisplay = stickyCartIndex !== null;

  usePushViewCartEventToGA(cart);

  const changeRoute = useCallback(
    (url: string) => {
      if (/^https?:\/\//.exec(url)) {
        window.open(url, "_blank");
        return;
      }
      dispatch(cartActions.toggleMyCart({}));
      history.push(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div css={classes.root}>
      <SPView>
        <CartStickyOrder
          isDisplay={isDisplay}
          // @ts-expect-error TS2322
          orderCart={orderCarts[stickyCartIndex]}
          goSettlement={() =>
            // @ts-expect-error TS2532
            changeRoute(orderCarts[stickyCartIndex].purchaseLink)
          }
        />
      </SPView>

      <div
        css={classes.closeIcon}
        onClick={() => {
          dispatch(cartActions.toggleMyCart({}));
        }}
      >
        <CloseIcon />
      </div>

      <div css={classes.heading}>
        <CartHeading numberOfProducts={cartItemCount} />
      </div>

      <RemovedCartItemsMessage />

      {orderCarts.map((cart, index) => {
        const {
          heading,
          shippingAtTitle,
          products,
          shippingFee,
          subTotalPrice,
          purchaseLink,
        } = cart;
        return (
          <div
            css={classes.product}
            key={`cart-${index}`}
            ref={headerRefs.current[index]}
          >
            <CartOrderHeading
              cartNumber={heading}
              date={shippingAtTitle}
              total={subTotalPrice}
              shippingFee={shippingFee}
              goSettlement={() => {
                changeRoute(purchaseLink);
              }}
            />

            {products.map((product, index) => {
              return (
                <React.Fragment key={index}>
                  <CartOrderProduct
                    product={product}
                    deleteFromCart={() => deleteFromCart(product)}
                    changeRoute={changeRoute}
                  />
                </React.Fragment>
              );
            })}
          </div>
        );
      })}

      <div
        css={classes.closeButton}
        onClick={() => {
          dispatch(cartActions.toggleMyCart({}));
        }}
      >
        {t("cart.button.closeButton")}
      </div>
    </div>
  );
};

const styles = () => ({
  root: css`
    position: relative;
    width: 90%;
    max-width: 980px;
    padding: 40px 0;
    margin: 0 auto;
  `,
  closeIcon: css`
    position: absolute;
    top: 12px;
    right: 0;
    cursor: pointer;
    @media screen and (min-width: 768px) {
      top: 40px;
    }
    svg {
      @media screen and (min-width: 768px) {
        width: 32px;
        height: 32px;
      }
    }
  `,
  heading: css`
    margin-bottom: 16px;
  `,
  product: css`
    margin-bottom: 24px;
  `,
  closeButton: css`
    width: 100%;
    max-width: 400px;
    padding: 8px;
    margin: 40px auto 0;
    color: #00c2ad;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    letter-spacing: 1px;
    border: 2px solid #00c2ad;
    cursor: pointer;
    @media screen and (min-width: 768px) {
      font-size: 16px;
    }
  `,
});
